import { newId } from '../domain/ids';
import { scopedStorageKey } from '../persistence/accountScope';
import { V2_COMMENT_LIBRARY_KEY } from '../persistence/persistenceContract';
import { createStore, useStore } from './createStore';

// 「我的评语」 (Owner decision list 2026-10-19 item 33): the inspector's own reusable wording — saved from any
// text field with one tap, inserted into any text field from the picker, managed and exported in Settings.
// Per account, on the device (like the inspector profile); it is the inspector's own text, never AI output.
export interface SavedComment {
  id: string;
  text: string;
  usedCount: number;
  createdAt: number;
  updatedAt: number;
}

export const MAX_COMMENTS = 500;
export const MAX_COMMENT_CHARS = 2000;

export const commentLibraryStore = createStore<{ comments: SavedComment[] | null }>({ comments: null });

function isComment(value: unknown): value is SavedComment {
  const c = value as SavedComment;
  return typeof c === 'object' && c !== null && typeof c.id === 'string' && typeof c.text === 'string' && typeof c.usedCount === 'number' && typeof c.createdAt === 'number' && typeof c.updatedAt === 'number';
}

export function readComments(): SavedComment[] {
  const cached = commentLibraryStore.get().comments;
  if (cached) return cached;
  let comments: SavedComment[] = [];
  try {
    const key = scopedStorageKey(V2_COMMENT_LIBRARY_KEY);
    if (key !== null) {
      const parsed = JSON.parse(window.localStorage.getItem(key) ?? '[]') as unknown;
      comments = Array.isArray(parsed) ? parsed.filter(isComment) : [];
    }
  } catch {
    comments = [];
  }
  commentLibraryStore.set(() => ({ comments }));
  return comments;
}

function write(comments: SavedComment[]): SavedComment[] {
  try {
    const key = scopedStorageKey(V2_COMMENT_LIBRARY_KEY);
    if (key !== null) window.localStorage.setItem(key, JSON.stringify(comments));
  } catch {
    // a refused write keeps the list in memory for this session
  }
  commentLibraryStore.set(() => ({ comments }));
  return comments;
}

// Most used first, then the newest.
export function sortedComments(comments: readonly SavedComment[]): SavedComment[] {
  return [...comments].sort((a, b) => b.usedCount - a.usedCount || b.updatedAt - a.updatedAt || a.id.localeCompare(b.id));
}

export function useComments(): SavedComment[] {
  const comments = useStore(commentLibraryStore, (s) => s.comments);
  return comments ?? readComments();
}

export type AddCommentOutcome = 'added' | 'exists' | 'empty' | 'full';

export function addCommentAction(text: string, now: number = Date.now()): AddCommentOutcome {
  const clean = text.trim().slice(0, MAX_COMMENT_CHARS);
  if (clean.length === 0) return 'empty';
  const comments = readComments();
  if (comments.some((c) => c.text === clean)) return 'exists';
  if (comments.length >= MAX_COMMENTS) return 'full';
  write([...comments, { id: newId('cmt'), text: clean, usedCount: 0, createdAt: now, updatedAt: now }]);
  return 'added';
}

export function updateCommentAction(id: string, text: string, now: number = Date.now()): boolean {
  const clean = text.trim().slice(0, MAX_COMMENT_CHARS);
  if (clean.length === 0) return false;
  const comments = readComments();
  if (!comments.some((c) => c.id === id)) return false;
  write(comments.map((c) => (c.id === id ? { ...c, text: clean, updatedAt: now } : c)));
  return true;
}

export function removeCommentAction(id: string): void {
  write(readComments().filter((c) => c.id !== id));
}

export function markCommentUsed(id: string): void {
  write(readComments().map((c) => (c.id === id ? { ...c, usedCount: c.usedCount + 1 } : c)));
}

// Inserting a saved comment: on its own line after what is already written.
export function insertComment(current: string, comment: string): string {
  return current.trim().length === 0 ? comment : `${current.replace(/\s+$/, '')}\n${comment}`;
}

// The export (「可导出」): a plain-text file, one numbered comment per block.
export function commentsExportText(comments: readonly SavedComment[]): string {
  return sortedComments(comments)
    .map((c, index) => `${index + 1}. ${c.text}`)
    .join('\n\n');
}

export function resetCommentLibraryForTests(): void {
  commentLibraryStore.set(() => ({ comments: null }));
}
